import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Heart, ShoppingBag, Eye } from "lucide-react";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { useState } from "react";

function StarRow({ rating = 4.5, count = 0 }) {
  const stars = Math.round(rating);
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={12}
            className={i < stars ? "text-accent" : "text-slate-300"}
            fill="currentColor"
          />
        ))}
      </div>
      {count > 0 && <span className="text-[11px] text-slate-500">({count})</span>}
    </div>
  );
}

export default function ProductCard({ book, index = 0 }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const [wishlisted, setWishlisted] = useState(false);

  const price = Number(book.price || 0);
  const original = book.original_price
    ? Number(book.original_price)
    : (book.discount_percent ? price / (1 - book.discount_percent / 100) : null);
  const discount = original && original > price ? Math.round(((original - price) / original) * 100) : 0;
  const stock = book.stock ?? 0;
  const inStock = stock > 0;
  const lowStock = inStock && stock <= 5;

  const rating = Number(book.rating) || 0;
  const reviewCount = Number(book.review_count) || 0;
  const defaultFormat = (Array.isArray(book.formats) && book.formats[0]) || "physical";

  const goToDetail = () => navigate(`/book/${book.id}`);

  const addToCart = (e) => {
    e.stopPropagation();
    if (!inStock) return;
    const ok = add(book, defaultFormat, { openSheet: true });
    if (ok) toast.success(`${book.title} added to cart`);
  };

  const toggleWishlist = (e) => {
    e.stopPropagation();
    setWishlisted((v) => !v);
    toast.success(wishlisted ? "Removed from wishlist" : "Added to wishlist");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay: Math.min(index, 8) * 0.04 }}
    >
      <div
        role="group"
        data-testid={`product-card-${book.id}`}
        aria-label={book.title}
        className="group card-elevate p-3 md:p-4 h-full flex flex-col"
      >
        {/* Cover — clickable to detail via keyboard/mouse */}
        <button
          type="button"
          onClick={goToDetail}
          aria-label={`View ${book.title}`}
          className="relative aspect-book bg-slate-100 rounded-lg product-image-wrap mb-4 block w-full text-left"
        >
          {book.cover_image ? (
            <img
              src={book.cover_image}
              alt={book.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-300 text-xs uppercase font-semibold tracking-wider">
              No cover
            </div>
          )}

          {discount > 0 && (
            <span className="absolute top-2 left-2 badge-discount" data-testid={`discount-${book.id}`}>
              -{discount}%
            </span>
          )}
          {book.featured && !discount && (
            <span className="absolute top-2 left-2 bg-secondary text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md">
              Bestseller
            </span>
          )}

          <div className="absolute inset-x-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-white/95 backdrop-blur rounded-lg px-3 py-2 text-center text-xs font-semibold text-slate-900 flex items-center justify-center gap-1.5">
              <Eye size={12} /> Quick view
            </div>
          </div>
        </button>

        {/* Wishlist — floated absolute over cover but as sibling for a11y */}
        <button
          type="button"
          onClick={toggleWishlist}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          data-testid={`wishlist-${book.id}`}
          className={`absolute top-6 right-6 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center transition-colors z-10 ${
            wishlisted ? "text-red-500" : "text-slate-500 hover:text-red-500"
          }`}
          style={{ marginTop: "0.25rem", marginRight: "0.25rem" }}
        >
          <Heart size={15} fill={wishlisted ? "currentColor" : "none"} />
        </button>

        <div className="space-y-1.5 flex-1">
          {book.author && (
            <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wider truncate">by {book.author}</p>
          )}
          <Link
            to={`/book/${book.id}`}
            className="block font-semibold text-sm md:text-[15px] text-slate-900 leading-snug line-clamp-2 hover:text-primary transition-colors min-h-[2.6em]"
          >
            {book.title}
          </Link>
          {reviewCount > 0 && <StarRow rating={rating} count={reviewCount} />}
          <div className="flex items-baseline gap-2">
            <span className="font-heading font-bold text-lg text-slate-900" data-testid={`price-${book.id}`}>
              ${price.toFixed(2)}
            </span>
            {original && original > price && (
              <span className="text-xs text-slate-400 line-through">${original.toFixed(2)}</span>
            )}
          </div>
          <div>
            {!inStock ? (
              <span className="badge-oos">Out of stock</span>
            ) : lowStock ? (
              <span className="badge-lowstock">Only {stock} left</span>
            ) : (
              <span className="badge-instock">In stock · Ships in 24h</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={addToCart}
          disabled={!inStock}
          data-testid={`add-to-cart-${book.id}`}
          className={`mt-3 w-full h-10 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
            inStock
              ? "bg-primary hover:bg-secondary text-white active:scale-[0.98]"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          <ShoppingBag size={15} /> {inStock ? "Add to cart" : "Notify me"}
        </button>
      </div>
    </motion.div>
  );
}
